import { Clipboard } from '@angular/cdk/clipboard';
import { inject, Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { AppService } from '@app/app.service';
import { CustomValidators } from '@app/app.validator';
import { AppHelper } from '@app/core/app.helper';
import { EDateFormat, EDateType, EShowTypes } from '@app/core/enums';
import { mbHasMatch, mbNormalize } from '@fui/fui-helpers';
import { getStringPresentation as getAddressPresentation } from '@helpers/address-helper';
import { ElistDateFormat, EOperationType } from '@modules/factor/children/constructor/enums/constructor.enums';
import { DateStatusEnum } from '@modules/factor/children/scheduler/enums/scheduler.enums';
import { IRegistryReportingDate } from '@modules/factor/children/scheduler/interfaces/scheduler.interface';
import { EFactorFiledTypes } from '@modules/factor/enums/factor.enums';
import { LangService } from '@services/lang.service';
import { ENotifyTypes, NotifierService } from '@services/notifier.service';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { formatDecimalPrecision, parseFormattedNumber } from 'src/shared/helpers/number-helper';
import {
    InterfaceConfigData,
    InterfaceFactorDateType,
    InterfaceFactorFieldsTypes,
} from '../interfaces/factor-row.interface';

@Injectable({
    providedIn: 'root',
})
export class FactorRowService extends AppService {
    public readonly langService = inject(LangService);

    /** Массив файлов на удаление */
    removeFileUUids: string[] = [];

    className = 'FactorRowService';

    /** Разрешить массовое редактирование для кнопки */
    isMassEditAllowed = false;

    /** URL страницы */
    url = '/factor/front_row';

    /** URL работы с файлами */
    fileUploadUrl: string = this.serverUrl + '/files/front_files/file_upload';

    /** ИД реестра */
    factorId: number = null;
    /** Имя реестра */
    factorName: string = null;
    /** ИД данных реестра */
    factorDataId: string = null;
    /** Имя данных(карточки) реестра */
    factorDataName: string = null;

    private factorDataSubject = new BehaviorSubject<any>(null);
    factorData$ = this.factorDataSubject.asObservable();

    /**
     * Обновление данных о реестре и его карточки
     * @param data
     */
    updateFactorData(data: any) {
        this.factorDataSubject.next(data);
    }

    /**
     * Получение значения поля даты в полном формате
     * @param fieldValue значение
     * @param field поле
     * @param type тип даты
     */
    getFullDateValue(fieldValue: any, field: any, type: string): moment.Moment {
        let result = null;
        if (fieldValue && fieldValue[type]) {
            let m = null;
            try {
                m = moment(fieldValue[type], 'DD.MM.YYYY');
            } catch (e) {}
            if (m && m.isValid()) {
                result = m;
            }
        } else if (Number(field.is_current) === 1) {
            result = moment();
        }
        return result ? result.format('YYYY-MM-DD') : null;
    }

    /**
     * Получение значения поля даты с временем в полном формате
     * @param fieldValue значение
     * @param field поле
     * @param type тип даты
     */
    getFullDateTimeValue(fieldValue: any, field: any, type: string): moment.Moment {
        let result = null;
        if (fieldValue && fieldValue[type]) {
            let m = null;
            try {
                m = moment(fieldValue[type], 'DD.MM.YYYY HH:mm');
            } catch (e) {}
            if (m && m.isValid()) {
                result = m;
            }
        } else if (Number(field.is_current) === 1) {
            result = moment();
        }

        return result ? result.format('YYYY-MM-DD HH:mm') : null;
    }

    /**
     * Получение значения поля даты в сокращенном формате
     * @param fieldValue значение
     * @param field поле
     * @param typeM тип даты для месяца
     * @param typeY тип даты для года
     * @param date
     */
    getDateMYValue(fieldValue: any, field: any, typeM: string, typeY: string, date: string = null): moment.Moment {
        if (fieldValue && (fieldValue[typeM] || fieldValue[typeY])) {
            const m = moment([
                fieldValue[typeY] ? fieldValue[typeY] : 2000,
                fieldValue[typeM] ? fieldValue[typeM] - 1 : 0,
                fieldValue[date] ? fieldValue[date] : 1,
            ]);

            if (m.isValid()) {
                return m;
            }
        }

        if (Number(field.is_current) === 1) {
            return moment();
        }

        return null;
    }

    /**
     * Получение значения языкового поля
     * @param fieldValue значение
     * @param field поле
     * @param index индекс накопительного поля
     * @param key ключ данных
     */
    getLanguageValue(fieldValue: any, field: any, index: number, key: string): object {
        const value = {};

        if (!field.language || field.language?.length < 1) {
            return value;
        }

        if (index === null) {
            value[field.id] = {};
            field.language.forEach((lang) => {
                value[field.id][lang.language] = lang[key];
            });
        } else if (fieldValue) {
            field.language.forEach((lang) => {
                if (fieldValue.fid === lang?.property_id) {
                    if (!value[field.id]) {
                        value[field.id] = {};
                    }
                    value[field.id][lang.language] = lang[key];
                }
            });
        }
        return value;
    }

    /**
     * Получение типа даты выбранного параметра
     * @param field параметр
     */
    getDateType(field) {
        if (field && field.date_format && field.date_format === EDateFormat.year) {
            return 'date-year';
        }
        if (field && field.date_format && field.date_format === EDateFormat.monthYear) {
            return 'date-month';
        }
        if (field && field.date_format && field.date_format === EDateFormat.dateTime) {
            return 'date-time';
        }
        if (field && field.date_format && field.date_format === EDateFormat.month) {
            return 'month';
        }
        if (field && field.date_format && field.date_format === EDateFormat.dayMonth) {
            return 'day-month';
        }
        return 'date';
    }

    /**
     * Изменение значения цифрового поля
     * @param fieldVal данные поля
     * @param val текущее значение из формы
     * @param calcRightFieldId Список id изменяемых полей
     * @param digitFields Список полей типа digit
     * @param notifier сервис всплывающих сообщений
     */
    changeDigitValue(fieldVal, val, calcRightFieldId: any, digitFields: any, notifier: NotifierService) {
        Object.keys(calcRightFieldId).forEach((keys) => {
            if (calcRightFieldId[keys]) {
                calcRightFieldId[keys].forEach((key) => {
                    if (key.id === fieldVal.id) {
                        const props = key.fieldProps;
                        if (
                            !props ||
                            !props.calc_right ||
                            props.calc_right.length === 0 ||
                            !props.calc_props ||
                            props.calc_props.length === 0
                        ) {
                            return;
                        }
                        const terms = props.calc_right.split(' ');
                        let expr = '';
                        //Props.calc_props
                        for (let i = 0; i < terms.length; i++) {
                            // Если это значение показателя
                            if (terms[i]) {
                                if (terms[i][0] === '{' && terms[i][terms[i].length - 1] === '}') {
                                    let fid = terms[i].substr(1, terms[i].length - 2);
                                    const fidDef = fid.split('|');
                                    let defaultValue = null;
                                    if (fidDef.length > 1) {
                                        fid = parseInt(fidDef[0], 10);
                                        defaultValue = fidDef[1] + '';
                                        defaultValue = parseFormattedNumber(
                                            defaultValue,
                                            this.langService.currentLangCode.replace('_', '-'),
                                        );
                                    } else {
                                        fid = parseInt(fid, 10);
                                    }
                                    //++ получаем коэф. ед. изм.
                                    const ratio = props.calc_props[fid];
                                    let value;
                                    try {
                                        value = fid === key.id ? val : digitFields[fid].value;
                                    } catch (e) {
                                        value = 0;
                                    }
                                    let fieldValue = 0;
                                    if (value) {
                                        try {
                                            value = value + '';
                                            value = parseFormattedNumber(
                                                value,
                                                this.langService.currentLangCode.replace('_', '-'),
                                            );
                                            fieldValue = value / ratio;
                                        } catch (e) {
                                            fieldValue = 0;
                                        }
                                        expr += fieldValue;
                                    } else if (defaultValue !== '') {
                                        expr += defaultValue;
                                    }
                                } else if (terms[i][0] === '[' && terms[i][terms[i].length - 1] === ']') {
                                    const operation = terms[i].substr(1, terms[i].length - 2);
                                    switch (operation) {
                                        case '+':
                                            expr += ' ' + operation + ' ';
                                            break;

                                        case '-':
                                            expr += ' ' + operation + ' ';
                                            break;

                                        case '*':
                                            expr += ' ' + operation + ' ';
                                            break;

                                        case '/':
                                            expr += ' ' + operation + ' ';
                                            break;
                                        case '%':
                                            expr = ' ( ' + expr + ' ) * 100.0 ';
                                            break;
                                    }
                                } else {
                                    expr += ' ' + terms[+i] + ' ';
                                }
                            }
                        }
                        try {
                            // eslint-disable-next-line no-eval
                            let result = eval(expr);
                            // Если попытка деления на 0, то зануляем
                            result = result === Infinity ? 0 : result;

                            // Для целочисленных округляем до целого
                            if (props.number_type === 'int') {
                                result = formatDecimalPrecision(result, 0);
                            }

                            // Для вещественных округляем с указанной точностью
                            if (props.number_type === 'float') {
                                const precision = props.precision || 2;
                                result = formatDecimalPrecision(result, precision);
                            }
                            key.parentField.setValue(result);
                        } catch (e) {
                            notifier.notify('error', this.language_dictionary.mistake_in_calculations);
                        }
                    }
                });
            }
        });
    }

    /**
     * Проверка отображения поля на форме
     * @param id ид поля
     * @param conditions условия полей
     * @param form Значения формы
     */
    isViewField(id: string, form: FormGroup, conditions: any): boolean {
        return this.getShowStatus(id, form, conditions);
    }

    /**
     * Проверка отображения по родителям формы
     * @param parents список родителей
     * @param conditions условия отображения полей
     * @param form Значения формы
     */
    isViewParents(parents: string[], form: FormGroup, conditions: any) {
        for (const id of parents) {
            if (!this.getShowStatus(id, form, conditions)) {
                return false;
            }
        }
        return true;
    }

    /**
     * Получение статуса отображения/обязательности элемента
     * @param conditions условия полей
     * @param form Значения формы
     * @param id идентификатор показателя
     */
    private getShowStatus(id: string, form: FormGroup, conditions: any): boolean {
        if (!conditions[id]) {
            return true;
        }
        const result = this.checkEntry(conditions[id], form);
        return result;
    }

    /**
     * Проставление валидации для поля фомы
     * @param form форма
     * @param id ид показателя
     * @param isVisible отображается ли поле
     * @param isRequired обязательное ли поле
     * @param allFieldsForm Информация по полям формы
     * @param fieldsTypes Типы полей
     * @param displayConditions условия отображения полей
     * @param requiredConditions условия обязательности полей
     */
    setAllPropertiesValidation(
        form: FormGroup | any,
        id: string,
        isVisible: boolean,
        isRequired: boolean,
        allFieldsForm: { [key: string]: any },
        fieldsTypes: InterfaceFactorFieldsTypes,
        displayConditions: { [key: string]: { [key: string]: object } },
        requiredConditions: { [key: string]: { [key: string]: object } },
    ) {
        const formControl = form?.get(id) ?? null;
        const item = allFieldsForm[id] ?? null;
        if (!formControl || !item) {
            return;
        }
        if (item.is_folder === '1' && Number(item.show_type) === EShowTypes.storyForm) {
            if (formControl.controls) {
                Object.keys(formControl.controls).forEach((key) => {
                    item.properties.forEach((property) => {
                        this.setAllPropertiesValidation(
                            formControl.get(key),
                            property.id,
                            isVisible,
                            isRequired,
                            allFieldsForm,
                            fieldsTypes,
                            displayConditions,
                            requiredConditions,
                        );
                    });
                    formControl.get(key)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                });
                formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
        } else if (item.is_folder === '1' && Number(item.show_type) !== EShowTypes.storyForm) {
            if (Number(item.show_type) === EShowTypes.alternativeForm && item.is_required === '1') {
                this.setFieldValidate(formControl, isVisible);
            }
            item.properties.forEach((property) => {
                this.setAllPropertiesValidation(
                    form,
                    property.id,
                    isVisible,
                    isRequired,
                    allFieldsForm,
                    fieldsTypes,
                    displayConditions,
                    requiredConditions,
                );
            });
        } else {
            if (isVisible && item.parents && item.parents?.length) {
                isVisible = this.isViewParents(item.parents, form, displayConditions);
            }
            const required = isVisible && isRequired;
            formControl.required = required;
            if (item.data_type === fieldsTypes.string || item.data_type === fieldsTypes.text) {
                this.setFieldValidate(formControl.get(this.defaultLanguage), required);
                formControl.get(this.defaultLanguage).updateValueAndValidity();
            } else if (item.data_type === fieldsTypes.link) {
                this.setFieldValidate(formControl.get('url')?.get(this.defaultLanguage), required);
                formControl.get('url')?.get(this.defaultLanguage).updateValueAndValidity();
            } else if (formControl.controls && Object.keys(formControl.controls).length) {
                Object.keys(formControl.controls).forEach((key) => {
                    this.setFieldValidate(formControl.get(key), required);
                    formControl.get(key).updateValueAndValidity();
                });
            } else if (item.data_type === fieldsTypes.relation) {
                const validators = required ? [CustomValidators.relationFieldValidator()] : [];
                formControl.setValidators(validators);
                formControl.updateValueAndValidity();
            } else {
                this.setFieldValidate(formControl, required);
                formControl.updateValueAndValidity();
            }
        }
    }

    /**
     * Проставление валидации полю
     * @param form форма
     * @param required обязательное ли поле
     */
    setFieldValidate(form: FormGroup, required: boolean) {
        if (required) {
            form?.addValidators([Validators.required]);
        } else {
            form?.removeValidators([Validators.required]);
        }
    }

    /**
     * Проверка на выполнение условий для поля
     * @param viewsConditions - условия отображения/обязательности поля
     * @param form значения формы
     */
    checkEntry(viewsConditions: any, form: FormGroup) {
        let result = true; // Изначально предполагаем, что все условия выполнены
        let groupResult = true;
        let conditionResult = true;
        let cOperator: 'and' | 'or' = null;
        let gOperator: 'and' | 'or' = null;
        // Проходимся по группам
        for (const index in viewsConditions) {
            if (viewsConditions.hasOwnProperty(index)) {
                const group = viewsConditions[index];

                // Проходимся по условиям
                for (const indexC in group.conditions) {
                    if (group.conditions.hasOwnProperty(indexC)) {
                        const condition = group.conditions[indexC];
                        // Извлечение параметров из условия
                        const fieldId = condition.field_id;
                        const operation = condition.operation;
                        const cFieldType = condition.field_type;
                        // Значение поля
                        const formControl = form?.get(fieldId) as any;
                        let fieldValue;
                        if (formControl) {
                            fieldValue =
                                formControl.relation_value !== undefined
                                    ? formControl.relation_value
                                    : formControl.value;
                        }
                        if (fieldValue === undefined) {
                            continue;
                        }
                        let fieldValueSecond = null;
                        let conditionValueMain = condition.value.main_value;
                        let conditionValueSecond = condition.value.second_value;

                        if (cFieldType === EFactorFiledTypes.address) {
                            fieldValue = getAddressPresentation(fieldValue, null);
                        }

                        const isPeriod =
                            fieldValue && fieldValue.hasOwnProperty('to') && fieldValue.hasOwnProperty('from');

                        const isEmpty =
                            fieldValue === null ||
                            fieldValue === '' ||
                            (Array.isArray(fieldValue)
                                ? fieldValue.length === 0
                                : typeof fieldValue === 'object'
                                  ? Object.keys(fieldValue).length === 0 ||
                                    (isPeriod && (!fieldValue.to || !fieldValue.from))
                                  : !fieldValue);
                        if (isEmpty) {
                            fieldValue = null;
                        }

                        if (condition.field_date_format !== null) {
                            condition.field_date_format = String(condition.field_date_format);
                        }

                        // Преобразование в нужные форматы
                        if (cFieldType === EFactorFiledTypes.date) {
                            const isDuration =
                                operation === EOperationType.durationEqual ||
                                operation === EOperationType.durationLess ||
                                operation === EOperationType.durationMore;
                            const parseFormat =
                                condition.field_date_format === EDateFormat.month ? 'M' : 'YYYY-MM-DD HH:mm:ss';
                            if (conditionValueMain && !isDuration) {
                                conditionValueMain = moment(conditionValueMain, parseFormat);
                            }
                            if (conditionValueSecond && !isDuration) {
                                conditionValueSecond = moment(conditionValueSecond, parseFormat);
                            }
                            if (fieldValue !== null && isPeriod) {
                                fieldValueSecond = fieldValue.to || null;
                                fieldValue = fieldValue.from || null;
                            }
                            if (fieldValue !== null && moment(fieldValue, parseFormat).isValid()) {
                                fieldValue = moment(fieldValue, parseFormat);
                            }
                            if (fieldValueSecond !== null && moment(fieldValueSecond, parseFormat).isValid()) {
                                fieldValueSecond = moment(fieldValueSecond, parseFormat);
                            }

                            let compareFormat = 'YYYYMMDDHHmm';
                            switch (condition.field_date_format) {
                                case EDateFormat.date:
                                    compareFormat = 'YYYYMMDD';
                                    break;
                                case EDateFormat.dayMonth:
                                    compareFormat = '1MMDD';
                                    break;
                                case EDateFormat.monthYear:
                                    compareFormat = 'YYYYMM';
                                    break;
                                case EDateFormat.year:
                                    compareFormat = 'YYYY';
                                    break;
                                case EDateFormat.month:
                                    compareFormat = 'M';
                                    break;
                            }

                            if (conditionValueMain) {
                                conditionValueMain = isDuration
                                    ? Number(conditionValueMain)
                                    : Number(conditionValueMain.format(compareFormat));
                            }
                            if (conditionValueSecond) {
                                conditionValueSecond = isDuration
                                    ? Number(conditionValueSecond)
                                    : Number(conditionValueSecond.format(compareFormat));
                            }
                            if (fieldValue && !isDuration) {
                                fieldValue = Number(fieldValue.format(compareFormat));
                            }
                            if (fieldValueSecond && !isDuration) {
                                fieldValueSecond = Number(fieldValueSecond.format(compareFormat));
                            }
                        }

                        if (cFieldType === EFactorFiledTypes.digit) {
                            conditionValueMain = Number(conditionValueMain);
                            conditionValueSecond = Number(conditionValueSecond);
                            if (!isEmpty) {
                                fieldValue = Number(fieldValue);
                            }
                        }

                        if (cFieldType === EFactorFiledTypes.list) {
                            if (typeof fieldValue === 'string' && fieldValue.length) {
                                fieldValue = fieldValue.split(',');
                            }
                        }

                        if (cFieldType === EFactorFiledTypes.bool) {
                            conditionValueMain = conditionValueMain === '1';
                        }

                        if (cFieldType === EFactorFiledTypes.link) {
                            fieldValue = fieldValue && fieldValue.url ? fieldValue.url[this.defaultLanguage] : '';
                        }

                        // Проверка в зависимости от типа условия
                        switch (operation) {
                            case EOperationType.filled:
                                // Заполнено
                                switch (cFieldType) {
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                        conditionResult = !isEmpty && !!fieldValue[this.defaultLanguage];
                                        break;
                                    case EFactorFiledTypes.link:
                                        conditionResult = !isEmpty && !!fieldValue;
                                        break;
                                    default:
                                        conditionResult = !isEmpty;
                                        break;
                                }
                                break;
                            case EOperationType.notFilled:
                                switch (cFieldType) {
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                        conditionResult = isEmpty || !fieldValue[this.defaultLanguage];
                                        break;
                                    case EFactorFiledTypes.link:
                                        conditionResult = isEmpty || !fieldValue;
                                        break;
                                    default:
                                        conditionResult = isEmpty;
                                        break;
                                }
                                break;
                            case EOperationType.match:
                                // Соответствует
                                switch (cFieldType) {
                                    case EFactorFiledTypes.address:
                                        conditionResult = !isEmpty && conditionValueMain === fieldValue;
                                        break;
                                    case EFactorFiledTypes.list:
                                        conditionResult =
                                            !isEmpty && AppHelper.arraysEqualUnordered(fieldValue, conditionValueMain);
                                        break;
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                        conditionResult =
                                            !isEmpty &&
                                            mbNormalize(conditionValueMain).trim() ===
                                                mbNormalize(fieldValue[this.defaultLanguage]).trim();
                                        break;
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_type) {
                                            case EDateType.single:
                                                conditionResult = !isEmpty && conditionValueMain === fieldValue;
                                                break;
                                            case EDateType.period:
                                                conditionResult =
                                                    !isEmpty &&
                                                    conditionValueMain === fieldValue &&
                                                    conditionValueSecond === fieldValueSecond;
                                                break;
                                        }
                                        break;
                                    default:
                                        conditionResult = !isEmpty && conditionValueMain === fieldValue;
                                        break;
                                }
                                break;
                            case EOperationType.notMatch:
                                // Не соответствует
                                switch (cFieldType) {
                                    case EFactorFiledTypes.address:
                                        conditionResult = isEmpty || conditionValueMain !== fieldValue;
                                        break;
                                    case EFactorFiledTypes.list:
                                        conditionResult =
                                            isEmpty || !AppHelper.arraysEqualUnordered(fieldValue, conditionValueMain);
                                        break;
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                        conditionResult =
                                            isEmpty ||
                                            mbNormalize(conditionValueMain).trim() !==
                                                mbNormalize(fieldValue[this.defaultLanguage]).trim();
                                        break;
                                    case EFactorFiledTypes.date:
                                        switch (condition.field_date_type) {
                                            case EDateType.single:
                                                conditionResult = isEmpty || conditionValueMain !== fieldValue;
                                                break;
                                            case EDateType.period:
                                                conditionResult =
                                                    isEmpty ||
                                                    conditionValueMain !== fieldValue ||
                                                    conditionValueSecond !== fieldValueSecond;
                                                break;
                                        }
                                        break;
                                    default:
                                        conditionResult = isEmpty || conditionValueMain !== fieldValue;
                                        break;
                                }
                                break;
                            case EOperationType.Include:
                                // Содержит хотя бы одно из значений
                                switch (cFieldType) {
                                    case EFactorFiledTypes.list:
                                        conditionResult =
                                            !isEmpty && AppHelper.hasCommonElement(fieldValue, conditionValueMain);
                                        break;
                                }
                                break;
                            case EOperationType.notInclude:
                                // Не содержит ни одного из значений
                                switch (cFieldType) {
                                    case EFactorFiledTypes.list:
                                        conditionResult =
                                            isEmpty || !AppHelper.hasCommonElement(fieldValue, conditionValueMain);
                                        break;
                                }
                                break;
                            case EOperationType.contains:
                                // Содержит
                                switch (cFieldType) {
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                        conditionResult =
                                            !isEmpty &&
                                            mbHasMatch(fieldValue[this.defaultLanguage], conditionValueMain);
                                        break;
                                    case EFactorFiledTypes.address:
                                        conditionResult = !isEmpty && mbHasMatch(fieldValue, conditionValueMain);
                                        break;
                                    default:
                                        conditionResult = !isEmpty && mbHasMatch(fieldValue, conditionValueMain);
                                        break;
                                }
                                break;
                            case EOperationType.notContains:
                                // Не содержит
                                switch (cFieldType) {
                                    case EFactorFiledTypes.string:
                                    case EFactorFiledTypes.text:
                                        conditionResult =
                                            isEmpty ||
                                            !mbHasMatch(fieldValue[this.defaultLanguage], conditionValueMain);
                                        break;
                                    case EFactorFiledTypes.address:
                                        conditionResult = isEmpty || !mbHasMatch(fieldValue, conditionValueMain);
                                        break;
                                    default:
                                        conditionResult = isEmpty || !mbHasMatch(fieldValue, conditionValueMain);
                                        break;
                                }
                                break;
                            case EOperationType.more:
                                // Больше
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = !isEmpty && Number(fieldValue) > conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.moreOrEqual:
                                // Больше или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = !isEmpty && Number(fieldValue) >= conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.less:
                                // Меньше
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = !isEmpty && Number(fieldValue) < conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.lessOrEqual:
                                // Меньше или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.digit:
                                        conditionResult = !isEmpty && Number(fieldValue) <= conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.later:
                                // Позже
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        conditionResult =
                                            !isEmpty && conditionValueMain && fieldValue > conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.laterEqual:
                                // Позже или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        conditionResult = !isEmpty && fieldValue >= conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.earlier:
                                // Раньше
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        conditionResult = !isEmpty && fieldValue < conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.earlierEqual:
                                // Раньше или равно
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        conditionResult = !isEmpty && fieldValue <= conditionValueMain;
                                        break;
                                }
                                break;
                            case EOperationType.inInterval:
                                // Попадает в интервал
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        if (condition.field_date_type === EDateType.single) {
                                            conditionResult =
                                                !isEmpty &&
                                                fieldValue >= conditionValueMain &&
                                                fieldValue <= conditionValueSecond;
                                        } else if (condition.field_date_type === EDateType.period) {
                                            conditionResult =
                                                !isEmpty &&
                                                fieldValue >= conditionValueMain &&
                                                fieldValueSecond <= conditionValueSecond;
                                        }
                                        break;
                                    case EFactorFiledTypes.digit:
                                        conditionResult =
                                            !isEmpty &&
                                            fieldValue >= conditionValueMain &&
                                            fieldValue <= conditionValueSecond;
                                }
                                break;
                            case EOperationType.foundOne:
                                // Есть хотя бы один
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                    case EFactorFiledTypes.gallery:
                                    case EFactorFiledTypes.file_versions:
                                        conditionResult = Object.keys(fieldValue ?? {}).length > 0;
                                        break;
                                    case EFactorFiledTypes.relation:
                                        conditionResult = fieldValue?.length > 0;
                                        break;
                                }
                                break;
                            case EOperationType.notFoundOne:
                                // Нет ни одного
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                    case EFactorFiledTypes.gallery:
                                    case EFactorFiledTypes.file_versions:
                                        conditionResult = Object.keys(fieldValue ?? {}).length === 0;
                                        break;
                                    case EFactorFiledTypes.relation:
                                        conditionResult = !fieldValue?.length;
                                        break;
                                }
                                break;
                            case EOperationType.fileSigned:
                                // Все файлы подписаны
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                    case EFactorFiledTypes.gallery:
                                    case EFactorFiledTypes.file_versions:
                                        conditionResult = false;
                                        for (const key of Object.keys(fieldValue ?? {})) {
                                            const value = fieldValue[key];
                                            conditionResult = value.signs.length > 0;
                                            if (!conditionResult) {
                                                break;
                                            }
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.nameFileInclude:
                                // В названии или имени содержится
                                switch (cFieldType) {
                                    case EFactorFiledTypes.file:
                                    case EFactorFiledTypes.gallery:
                                    case EFactorFiledTypes.file_versions:
                                        conditionResult = false;
                                        for (const key of Object.keys(fieldValue ?? {})) {
                                            const value = fieldValue[key];
                                            conditionResult =
                                                conditionResult || mbHasMatch(value._name, conditionValueMain);
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.durationEqual:
                                // Продолжительность равна
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        if (isEmpty) {
                                            conditionResult = false;
                                            break;
                                        }
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                switch (conditionValueSecond) {
                                                    case ElistDateFormat.day:
                                                        const days = fieldValueSecond.diff(fieldValue, 'day', true);
                                                        conditionResult = days === conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.month:
                                                        const months = fieldValueSecond.diff(fieldValue, 'month', true);
                                                        conditionResult = months === conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.year:
                                                        const years = fieldValueSecond.diff(fieldValue, 'year', true);
                                                        conditionResult = years === conditionValueMain;
                                                        break;
                                                }
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.durationMore:
                                // Продолжительность более
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        if (isEmpty) {
                                            conditionResult = false;
                                            break;
                                        }
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                switch (conditionValueSecond) {
                                                    case ElistDateFormat.day:
                                                        const days = fieldValueSecond.diff(fieldValue, 'day', true);
                                                        conditionResult = days > conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.month:
                                                        const months = fieldValueSecond.diff(fieldValue, 'month', true);
                                                        conditionResult = months > conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.year:
                                                        const years = fieldValueSecond.diff(fieldValue, 'year', true);
                                                        conditionResult = years > conditionValueMain;
                                                        break;
                                                }
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.durationLess:
                                // Продолжительность менее
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        if (isEmpty) {
                                            conditionResult = false;
                                            break;
                                        }
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                switch (conditionValueSecond) {
                                                    case ElistDateFormat.day:
                                                        const days = fieldValueSecond.diff(fieldValue, 'day', true);
                                                        conditionResult = days < conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.month:
                                                        const months = fieldValueSecond.diff(fieldValue, 'month', true);
                                                        conditionResult = months < conditionValueMain;
                                                        break;
                                                    case ElistDateFormat.year:
                                                        const years = fieldValueSecond.diff(fieldValue, 'year', true);
                                                        conditionResult = years < conditionValueMain;
                                                        break;
                                                }
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.beginsInInterval:
                                // Начинается в интервале
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        if (isEmpty) {
                                            conditionResult = false;
                                            break;
                                        }
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult =
                                                    fieldValue >= conditionValueMain &&
                                                    fieldValue <= conditionValueSecond;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            case EOperationType.endInInterval:
                                // Заканчивается в интервале
                                switch (cFieldType) {
                                    case EFactorFiledTypes.date:
                                        if (isEmpty) {
                                            conditionResult = false;
                                            break;
                                        }
                                        switch (condition.field_date_format) {
                                            case EDateFormat.date:
                                            case EDateFormat.dateTime:
                                                conditionResult =
                                                    fieldValueSecond >= conditionValueMain &&
                                                    fieldValueSecond <= conditionValueSecond;
                                                break;
                                            case EDateFormat.dayMonth:
                                                break;
                                            case EDateFormat.monthYear:
                                                break;
                                            case EDateFormat.year:
                                                break;
                                            case EDateFormat.month:
                                                break;
                                        }
                                        break;
                                }
                                break;
                            default:
                                conditionResult = false; // Неподдерживаемый тип условия
                                break;
                        }

                        if (cOperator === 'and') {
                            groupResult = groupResult && conditionResult; // Объединение с "И"
                        } else if (cOperator === 'or') {
                            groupResult = groupResult || conditionResult; // Объединение с "ИЛИ"
                        } else {
                            groupResult = conditionResult; // Если оператора нет
                        }

                        // Если результат уже ложный и оператор "И", выходим из цикла условий
                        if ((!groupResult && cOperator === 'and') || condition.operator === null) {
                            break;
                        }
                        cOperator = condition.operator;
                    }
                }

                if (gOperator === 'and') {
                    result = result && groupResult; // Объединение с "И"
                } else if (gOperator === 'or') {
                    result = result || groupResult; // Объединение с "ИЛИ"
                } else {
                    result = groupResult; // Если оператора нет
                }

                // Если результат уже ложный и оператор "И", выходим из цикла групп
                if ((!result && gOperator === 'and') || group.operator === null) {
                    break;
                }
                gOperator = group.operator;
            }
        }
        return result; // Вернуть итоговый результат проверки условий
    }

    /**
     * Преобразование строки в дату
     * @param value значение
     */
    convertDateString(value) {
        let valueDate = null;
        if (typeof value === 'string') {
            try {
                if (value.length === 16) {
                    valueDate = moment(value, 'YYYY-MM-DD HH:mm').add(moment().utcOffset(), 'm').utc(false);
                } else if (value.length <= 2) {
                    valueDate = value;
                } else {
                    valueDate = moment(value, 'YYYY-MM-DD').add(12, 'hours').add(moment().utcOffset(), 'm').utc(false);
                }
            } catch (e) {}
        } else {
            valueDate = value;
        }
        return valueDate;
    }

    /**
     * Получение данных по полю даты
     * @param field данные поля
     * @param value значение
     * @param dateFormat Форматы дат
     * @param dateType Типы дат
     */
    getValueDate(field: any, value: any, dateType: InterfaceFactorDateType) {
        const result = {
            value: null,
            valueTo: null,
            valueFM: null,
            valueTM: null,
            valueFY: null,
            valueTY: null,
            valueFF: null,
            valueFT: null,
            valueFD: null,
            valueTD: null,
        };
        let from = null;
        let to = null;
        let valueDate = null;
        if (field.date_type === dateType.period) {
            from = value?.from ? this.convertDateString(value?.from) : null;
            to = value?.to ? this.convertDateString(value?.to) : null;
        } else {
            valueDate = value ? this.convertDateString(value) : null;
        }
        if (value && field.date_format && field.date_format === EDateFormat.date) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.value = from.format('DD.MM.YYYY');
                }
                if (to) {
                    result.valueTo = to.format('DD.MM.YYYY');
                }
            } else {
                result.value = valueDate ? valueDate.format('DD.MM.YYYY') : null;
            }
        } else if (value && field.date_format && field.date_format === EDateFormat.dateTime) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFF = from.format('DD.MM.YYYY HH:mm');
                }
                if (to) {
                    result.valueFT = to.format('DD.MM.YYYY HH:mm');
                }
            } else {
                result.valueFF = valueDate ? valueDate.format('DD.MM.YYYY HH:mm') : null;
            }
        } else if (value && field.date_format && field.date_format === EDateFormat.monthYear) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFM = from.format('MM');
                    result.valueFY = from.format('YYYY');
                }
                if (to) {
                    result.valueTM = to.format('MM');
                    result.valueTY = to.format('YYYY');
                }
            } else {
                result.valueFM = valueDate ? valueDate.format('MM') : null;
                result.valueFY = valueDate ? valueDate.format('YYYY') : null;
            }
        } else if (value && field.date_format && field.date_format === EDateFormat.month) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFM = from;
                }
                if (to) {
                    result.valueTM = to;
                }
            } else {
                result.valueFM = valueDate ? valueDate : null;
            }
        } else if (value && field.date_format && field.date_format === EDateFormat.year) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFY = from.format('YYYY');
                }
                if (to) {
                    result.valueTY = to.format('YYYY');
                }
            } else {
                result.valueFY = +valueDate ? valueDate?.format('YYYY') : null;
            }
        } else if (value && field.date_format === EDateFormat.dayMonth) {
            if (field.date_type === dateType.period) {
                if (from) {
                    result.valueFD = from.format('DD');
                    result.valueFM = from.format('MM');
                }
                if (to) {
                    result.valueTD = to.format('DD');
                    result.valueTM = to.format('MM');
                }
            } else {
                result.valueFD = valueDate?.format('DD') ?? null;
                result.valueFM = valueDate?.format('MM') ?? null;
            }
        }
        return result;
    }

    /**
     * Добавление языковых данных
     * @param languageData языковые данные
     * @param order порядок значений
     * @param parentId ид родительской записи
     * @param id ИД характеристики
     * @param data данные
     * @param keyField ключ поля
     */
    addLangData(languageData: any, order: number, parentId: string, id: string, data: any, keyField: string) {
        Object.keys(data).forEach((k) => {
            if (!languageData[id]) {
                languageData[id] = [];
            }
            const valueLang = {
                factor_id: id,
                language: k,
                pid: parentId,
                order,
            };
            valueLang[keyField] = data[k];
            languageData[id].push(valueLang);
        });
    }

    /**
     * Добавление языковых данных для поля типа УРЛ
     * @param languageData языковые данные
     * @param order порядок значений
     * @param parentId ид родительской записи
     * @param id ИД характеристики
     * @param data данные
     */
    public addLangLinkFieldData(languageData: any, order: number, parentId: string, id: string, data: any) {
        Object.keys(data?.url).forEach((k) => {
            if (!languageData[id]) {
                languageData[id] = [];
            }
            const valueLang = {
                factor_id: id,
                language: k,
                pid: parentId,
                order,
                link_url: null,
                link_name: null,
            };
            valueLang.link_url = data?.url[k];
            valueLang.link_name = data?.name[k];
            languageData[id].push(valueLang);
        });
    }

    /**
     * Проверка запрета на загрузку файлов (по источнику заявки)
     */
    isDownloadDenied(form: FormGroup, applicationSourceId: number, applicationSourceData: InterfaceConfigData) {
        const applicationSource = form.get('values').get(String(applicationSourceId)).value;
        return applicationSource === applicationSourceData.epgu || applicationSource === applicationSourceData.ais_mfc
            ? 0
            : 1;
    }

    /**
     * Сформировать ссылку на карточку и поместить ее в буфер обмена
     * @param clipboard - Буфер обмена
     * @param text - HTMLElement карточки
     * @param notifier - сервис уведомлений
     * @param type - только ссылка или ссылка + заголовок
     */
    public copyTitle(
        clipboard: Clipboard,
        text: HTMLElement,
        notifier: NotifierService,
        type: 'link' | 'titleAndLink' = 'link',
    ): void {
        let copyElement: string;
        let message: string;

        switch (type) {
            case 'link':
                copyElement = window.location.href;
                message = this.language_dictionary.row_title_copy_success;
                break;
            case 'titleAndLink':
                copyElement = (text?.innerText ? text?.innerText : '') + '\x20\n' + window.location.href;
                message = this.language_dictionary.row_title_and_link_copy_success;
                break;
        }

        clipboard.copy(copyElement);
        notifier.notify(ENotifyTypes.success, message);
    }

    /**
     * Установка данных по датам в форму
     * @param form - форма
     * @param formData - данные записи
     */
    public setShedulerDates(
        form: FormGroup,
        formData: { [key: string]: any; scheduler_dates?: IRegistryReportingDate[] },
    ): void {
        let dayBefore = null;
        let dayAfter = null;
        let nextReportingDate = null;
        let currentDoc = null;
        let index = -1;

        if (formData.scheduler_date) {
            currentDoc = moment(formData.scheduler_date, 'YYYY-MM-DD');
        }

        if (formData?.scheduler_dates && formData?.scheduler_dates?.length > 0) {
            if (formData.scheduler_date) {
                const date = moment(formData.scheduler_date, 'YYYYMMDD').format('YYYY-MM-DD');
                index = formData?.scheduler_dates.findIndex((row) => row?.date === date);
            } else {
                index = formData?.scheduler_dates.findIndex((row) => row?.reporting_date_status === 'current');
            }
        }

        if (currentDoc === null) {
            return;
        }

        form.get('reporting_date').setValue(currentDoc, { eventEmit: false });

        if (formData?.period_input_before && formData?.period_input_before > 0) {
            dayBefore = moment(currentDoc)?.add(-1 * Number(formData?.period_input_before), 'days');
            form.get('reporting_date_earlier').setValue(dayBefore, { eventEmit: false });
        }

        if (formData?.period_input_after && formData?.period_input_after > 0) {
            dayAfter = moment(currentDoc)?.add(Number(formData?.period_input_after), 'days');
            form.get('reporting_date_later').setValue(dayAfter, { eventEmit: false });
        }

        if (index > -1) {
            if (formData?.scheduler_dates[index + 1]) {
                nextReportingDate = moment(formData?.scheduler_dates[index + 1].date);
                form.get('reporting_date_next').setValue(nextReportingDate, { eventEmit: false });
            }

            if (formData?.scheduler_dates[index]?.reporting_date_status === DateStatusEnum.Current) {
                form.get('is_current').setValue(true, { eventEmit: false });
            }
        }
    }
}
